'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { trackAdobePageView } from '@/utils/adobe';
import { getIsAccountLoggedIn } from '@/utils/account';
import { SUPPORTED_LOCALES, DEFAULT_LOCALE, type SupportedLocale } from '@/constants/locales';

// Resolve the active locale from the URL: non-default locales carry a /{locale} prefix,
// while the default locale uses clean URLs. The root <html lang> is static and can't be trusted.
const getLocaleFromPathname = (path: string): string => {
  const firstSegment = path.split('/').filter(Boolean)[0]?.toLowerCase();
  return firstSegment && SUPPORTED_LOCALES.includes(firstSegment as SupportedLocale)
    ? firstSegment
    : DEFAULT_LOCALE;
};

// Tracks Adobe page-view events on App Router pathname changes only — shallow query-string updates are ignored
const AdobePageViewTracker = () => {
  const pathname = usePathname();

  useEffect(() => {
    if (!pathname) return;
    // Derive locale from the URL prefix so Spanish pages report Spanish (not the static html lang)
    const locale = getLocaleFromPathname(pathname);
    // Read the login cookie synchronously here so the very first page view reports the correct status
    const isLoggedIn = getIsAccountLoggedIn();
    trackAdobePageView({ locale, isLoggedIn });
  }, [pathname]);

  return null;
};

// Pushes Adobe page-view events on every real SPA route change — render once in the root layout
export default function AdobeAnalytics() {
  return <AdobePageViewTracker />;
}
